import {Record, List, Map} from 'immutable';
import moment from 'moment-timezone';

import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import {
    type IOpeningHours,
    type IChildRelation,
    OpeningHours,
    ChildRelation,
} from 'web-app/react/modals-container/child-pickup-modal/models/shared';

interface IChildFromLocation {
    checkedIn: boolean;
    childId: string;
    image: string;
    institutionId: string;
    name: string;
    openingHours: IOpeningHours[];
    relations: IChildRelation[];
    requirePickupInfo: boolean;
    timezone: string;
    statements?: Statement[];
    showPickupUntilExactClosingTime: boolean;
}
export interface PossibilitiesFromLocationResponse {
    children: IChildFromLocation[];
    employees: EmployeeFromLocation[];
    distance?: number;
}

export interface Statement {
    statementId: string;
    type: string;
    title: string;
}

export class ChildFromLocation extends Record<{
    childId: string;
    openingHours: OpeningHours;
    relations: List<ChildRelation>;
    institutionId: string;
    requirePickupInfo: boolean;
    pickupRelationId: string;
    pickupTime: string;
    behaviors: BehaviorsCollection;
    goHomeWithChildId: string;
    isUpdating: boolean;
    name: string;
    fullName?: string;
    image: string;
    checkedIn: boolean;
    justSignedInOut: boolean;
    timezone: string;
    statements?: Statement[];
    showPickupUntilExactClosingTime: boolean;
}>({
    childId: '',
    openingHours: new OpeningHours(),
    relations: List(),
    institutionId: '',
    requirePickupInfo: false,
    pickupRelationId: '',
    pickupTime: '',
    behaviors: new BehaviorsCollection([]),
    goHomeWithChildId: '',
    isUpdating: false,
    name: '',
    image: '',
    checkedIn: false,
    justSignedInOut: false,
    timezone: '',
    statements: undefined,
    showPickupUntilExactClosingTime: false,
}) {}

export class CheckinFromLocation extends Record<{
    childSignIns: Map<string, ChildFromLocation>;
    employeeSignIns: EmployeeSignIns;
    distance?: number;
}>({
    childSignIns: Map(),
    distance: undefined,
    employeeSignIns: [],
}) {}

export class CheckinFromQRCode extends Record<{
    childSignIns: Map<string, ChildFromLocation>;
    employeeSignIns: EmployeeSignIns;
    qrCodeValue: string;
}>({
    childSignIns: Map(),
    employeeSignIns: [],
    qrCodeValue: '',
}) {}

export const childCheckinFromAPI = (child: IChildFromLocation) => {
    const {
        childId,
        relations,
        requirePickupInfo,
        openingHours,
        institutionId,
        name,
        image,
        checkedIn,
        timezone,
        statements,
        showPickupUntilExactClosingTime,
    } = child;

    const isoWeekDay = moment().isoWeekday();
    return new ChildFromLocation({
        childId,
        name,
        image,
        requirePickupInfo,
        /**
         * Opening hours from the API is an array of time objects, where day follows the ISO week day convention
         * https://momentjs.com/docs/#/get-set/iso-weekday/
         * https://en.wikipedia.org/wiki/ISO_week_date, i.e. 1 = monday, 7 = sunday
         */
        openingHours: new OpeningHours(openingHours.find(openingHour => openingHour.day === isoWeekDay)), // opening hours for the current day
        relations: List(relations.map(r => new ChildRelation(r))),
        institutionId,
        checkedIn,
        timezone,
        statements,
        showPickupUntilExactClosingTime,
    });
};

export interface EmployeeGroup {
    groupId: string;
    image: string;
    title: string;
}
export interface EmployeeFromLocation {
    employeeId: string;
    checkedIn: boolean;
    checkedInGroup: EmployeeGroup | null;
    groups: Array<EmployeeGroup>;
    image: string;
    institution: {
        image: string;
        institutionId: string;
        title: string;
    };
    institutionId: string;
    name: string;
    statements?: Statement[];
    timezone?: string;
    isUpdating?: boolean;
}
export type EmployeeSignIns = EmployeeFromLocation[];

const fromAPI = (data: PossibilitiesFromLocationResponse) => {
    const {children, distance, employees} = data;

    return new CheckinFromLocation({
        childSignIns: children.reduce<Map<string, ChildFromLocation>>(
            (acc, child) => acc.set(child.childId, childCheckinFromAPI(child)),
            Map(),
        ),
        distance,
        employeeSignIns: employees,
    });
};

export default {
    record: CheckinFromLocation,
    default: new CheckinFromLocation(),
    fromAPI,
};
